import React from "react";
import { useTranslation } from "react-i18next";
import { Sparkles, MousePointerClick } from "lucide-react";

/**
 * Elegant instructions banner shown on the public game page, right below
 * the title + description and above the CTA card.
 *
 * Adapts dynamically to the required CTAs configured on the game:
 *   - 0 CTA  → nothing rendered
 *   - 1 CTA  → single-action sentence with the platform name highlighted
 *   - 2+ CTA → generic "complete the N actions below" sentence
 *
 * Then shows a subtle hint explaining the "Ouvrir → Prêt à jouer" flow.
 */
export default function CtaInstructionBanner({ ctas = [], merchantName = "", gameType = "", solid = false }) {
  const { t, i18n } = useTranslation();

  const required = ctas.filter((c) => c.required);
  // Show required CTAs first; if no required ones, list them all.
  const visible = required.length > 0 ? ctas : ctas;
  if (!visible.length) return null;

  const merchant = (merchantName || "").trim() || t("landing.hero_line1");

  // Registration-flavored wording for `registration_gift` games where
  // the participant is signing up to receive a physical reward rather
  // than playing a chance-based game.
  const isRegistration = gameType === "registration_gift";
  const eyebrowKey = isRegistration
    ? "play.cta_banner_eyebrow_registration"
    : "play.cta_banner_eyebrow";
  const singleKey = isRegistration
    ? "play.cta_banner_single_registration"
    : "play.cta_banner_single";
  const doubleKey = isRegistration
    ? "play.cta_banner_double_registration"
    : "play.cta_banner_double";
  const multiKey = isRegistration
    ? "play.cta_banner_multi_registration"
    : "play.cta_banner_multi";
  const hintKey = isRegistration
    ? "play.cta_banner_hint_registration"
    : "play.cta_banner_hint";

  function actionFor(cta) {
    const key = `play.cta_banner_action_${cta.type}`;
    return i18n.exists(key)
      ? t(key, { merchant })
      : (cta.label || "").toLowerCase();
  }

  let mainHtml = "";
  if (visible.length === 1) {
    mainHtml = t(singleKey, { action: actionFor(visible[0]) });
  } else if (visible.length === 2) {
    // Order: required first, optional second when applicable.
    const sorted = [...visible].sort(
      (a, b) => (b.required === true ? 1 : 0) - (a.required === true ? 1 : 0)
    );
    mainHtml = t(doubleKey, {
      action1: actionFor(sorted[0]),
      action2: actionFor(sorted[1]),
    });
  } else {
    mainHtml = t(multiKey, { count: visible.length });
  }

  const hintHtml = t(hintKey);

  return (
    <div
      className="mx-auto mt-6 max-w-md fade-up"
      data-testid="cta-instruction-banner"
    >
      <div
        className={`relative overflow-hidden rounded-2xl border border-white/10 px-5 py-5 backdrop-blur-xl ${solid ? "bg-neutral-900" : "bg-black/55"}`}
        style={{
          boxShadow:
            "inset 0 1px 0 rgba(255,255,255,0.08), 0 20px 40px -20px rgba(0,0,0,0.7)",
        }}
      >
        {/* Soft holographic accent bar */}
        <div
          aria-hidden
          className="absolute left-0 top-0 h-full w-[2px]"
          style={{
            background:
              "linear-gradient(180deg, transparent, rgba(168,85,247,0.7), rgba(255,184,0,0.7), transparent)",
          }}
        />

        <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.22em] text-amber-300">
          <Sparkles className="h-3.5 w-3.5" strokeWidth={2.5} />
          {t(eyebrowKey)}
        </div>

        <p
          className="mt-2 text-base leading-snug text-white/95 [&_b]:font-semibold [&_b]:text-white"
          dangerouslySetInnerHTML={{ __html: mainHtml }}
        />

        <div className="mt-3 flex items-start gap-2.5 rounded-xl border border-white/10 bg-black/30 px-3.5 py-3">
          <MousePointerClick className="mt-0.5 h-4 w-4 flex-none text-amber-300" />
          <p
            className="text-[13px] leading-snug text-white/80 [&_b]:font-semibold [&_b]:text-amber-200"
            dangerouslySetInnerHTML={{ __html: hintHtml }}
          />
        </div>
      </div>
    </div>
  );
}
